import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/spotlight/styles.css";
import "./ui.scss";
import { Center, MantineProvider } from "@mantine/core";
import { Routes } from "./Routes";
import { notifications, Notifications } from "@mantine/notifications";
import { useThemeStore } from "./stores/ThemeStore";
import { useEffect, useMemo, useState } from "react";
import { getApiDetailsDomain } from "./api/ResellerAPI";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
LicenseInfo.setLicenseKey(
  "e3da72810018c62e1f820883694e1cd9Tz0xMjE5NTAsRT0xNzk1MDQ2Mzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
);
import customParseFormat from "dayjs/plugin/customParseFormat";
import { modals, ModalsProvider } from "@mantine/modals";
import FallbackPage from "./pages/unprotected/errors/FallbackPage";
import CookieHandler from "./CookieHandler";
import { useUIStore } from "./stores/UIStore";
import { useLanguage } from "./stores/LanguageStore";
import { useSessionStore } from "./stores/UserStore";
import { authChannel, switchAuthChannel } from "./api/axios";
import { updateSelf } from "./api/UserAPI";
import BrowserNotSupported from "./components/View/BrowserNotSupported";
dayjs.extend(customParseFormat);

const detectLang = () => {
  const lang = navigator.language.split("-")[0]; // "en", "fr"...
  switch (lang) {
    case "fr":
      return "FRA";
    case "es":
      return "SPA";
    case "it":
      return "ITA";
    case "de":
      return "DEU";
    default:
      return "ENG";
  }
};

function App() {
  const theme = useThemeStore((s) => s.theme);
  const isMaintenance = useMemo(() => {
    return location.href.includes("maintenance");
  }, []);

  //#region Reseller
  const [loading, setLoading] = useState(isMaintenance ? false : true);

  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    let lang = params.get("lang");

    if (lang) {
      useLanguage.getState().changeLanguage(lang);
    } else {
      useLanguage.getState().changeLanguage(detectLang());
    }

    if (!isMaintenance) {
      getApiDetailsDomain().then((response) => {
        let reseller = response.data.api;
        const setTheme = useThemeStore.getState().setTheme;

        setTheme(
          reseller.name === "Magileads" ? null : "#" + reseller?.color_primary,
          reseller.name === "Magileads"
            ? null
            : "#" + reseller?.color_secondary,
        );
        // const defaultPrimaryColor = "#2b0a2a";
        // const defaultSecondaryColor = "#055ea3";
        // setTheme(defaultPrimaryColor, defaultSecondaryColor);

        // favicon
        var link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href =
          reseller?.name === "Magileads"
            ? "/favicons/favicon.ico"
            : reseller?.favicon_url;
        document.title = reseller?.name;

        setLoading(false);
        useUIStore.getState().setWindowLoading(true);

        // Chatwoot
        if (reseller?.name === "Magileads") {
          (function (d, t) {
            var BASE_URL = "https://app.chatwoot.com";
            var g = d.createElement(t),
              s = d.getElementsByTagName(t)[0];
            g.src = BASE_URL + "/packs/js/sdk.js";
            g.defer = true;
            g.async = true;
            s.parentNode.insertBefore(g, s);
            g.onload = function () {
              window.chatwootSDK.run({
                websiteToken: "3JpcGGTHAx4SAD469xDEAHxV",
                baseUrl: BASE_URL,
              });
            };
          })(document, "script");
        }
        let chatwootOption = reseller?.options?.find(
          (x) => x.name === "Chatwoot",
        );
        if (chatwootOption?.active) {
          (function (d, t) {
            var BASE_URL = chatwootOption?.variables?.BASE_URL;
            var g = d.createElement(t),
              s = d.getElementsByTagName(t)[0];
            g.src = BASE_URL + "/packs/js/sdk.js";
            g.defer = true;
            g.async = true;
            s.parentNode.insertBefore(g, s);
            g.onload = function () {
              window.chatwootSDK.run({
                websiteToken: chatwootOption?.variables?.WEBSITE_TOKEN,
                baseUrl: BASE_URL,
              });
            };
          })(document, "script");
        }
      });
    }

    useUIStore.getState().setWindowLoading(false);
    if (!navigator?.locks) {
      modals.open({
        title: "Browser Not Supported",
        centered: true,
        // These options prevent the user from closing the modal and using a broken app.
        withCloseButton: false,
        closeOnClickOutside: false,
        closeOnEscape: false,
        children: <BrowserNotSupported />,
      });
    }

    //#region Broadcast listener
    // --- Listener for the MAIN session ---
    const handleAuthMessage = (event) => {
      if (event.data.type === "REFRESH_SUCCESS") {
        console.log("new refresh data acquired");

        useSessionStore.getState().setSession(event.data.payload);
      }
      if (event.data.type === "REFRESH_FAILURE") {
        console.log("refresh failure occured");
        window.location.replace(
          `/login?destination=${
            window.location.pathname !== "/login"
              ? window.location.pathname
              : "/"
          }&reason=REFRESH_FAILURE`,
        );
      }
    };

    // --- Listener for the SWITCHED session ---
    const handleSwitchMessage = (event) => {
      if (event.data.type === "SWITCH_REFRESH_SUCCESS") {
        // Silently update the switched session in this tab
        useSessionStore.getState().setUserSwitch(event.data.payload);
        updateSelf();
      }
      if (event.data.type === "SWITCH_REFRESH_FAILURE") {
        console.log("switch refresh failed");
        // The switched session expired, clear it for this tab
        useSessionStore.getState().setUserSwitch(null);
        // Optionally show a notification
        notifications.show({
          message: "Switched user session ended.",
          color: "blue",
        });
      }
      if (event.data.type === "SWITCH_STOPPED") {
        useSessionStore.getState().setUserSwitch(null);
        updateSelf();
      }
    };

    // Attach both listeners
    authChannel.addEventListener("message", handleAuthMessage);
    switchAuthChannel.addEventListener("message", handleSwitchMessage);
    //#endregion

    // Cleanup both listeners when the component unmounts
    return () => {
      authChannel.removeEventListener("message", handleAuthMessage);
      switchAuthChannel.removeEventListener("message", handleSwitchMessage);
    };
  }, []);

  //#endregion

  return (
    <MantineProvider theme={theme}>
      {loading ? (
        <Center w={"100vw"} h={"100vh"}>
          <FallbackPage loading />
        </Center>
      ) : (
        <Routes />
      )}
    </MantineProvider>
  );
}

export default App;
